import { Fragment, useState } from 'react';
import { SearchOutlined, MessageOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Empty as AntdEmpty, Button, Input } from 'antd';
import debounce from 'lodash/debounce';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardProcsContext } from '@/entities/proc';
import {
  fetchGetOrganizationsSharedProcs,
  OrganizationShareResponse,
  useGetOrganizationByShareCode,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { ChatMode } from '@/shared/types';
import { PageTitleBanner } from '@/shared/ui/page-title-banner';
import { OrganizationChat } from '@/widgets/chat/OrganizationChat';
import { PageLayout, PageLayoutHeaderGoBackBtn } from '@/widgets/page-layout';
import { ProcCard } from '@/widgets/proc-card';

const PROCS_PER_PAGE = 50;

type PublicPageParams = {
  code: 'string';
};

export const OrganizationProcsPage = () => {
  const { isAuthenticated } = useAuth0();
  const { code: shareCode = '' } = useParams<PublicPageParams>();
  const navigate = useNavigate();
  const { dashboardGoBackLink } = useRouterContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [chatVisible, setChatVisible] = useState(false);

  const { data: organizationData, isLoading: organizationLoading } =
    useGetOrganizationByShareCode<OrganizationShareResponse>(
      { pathParams: { shareCode } },
      {
        enabled: Boolean(shareCode),
        onError: () => isAuthenticated && navigate('/dashboard'),
      },
    );

  const {
    data: procsData,
    refetch: refetchProcs,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery([{ searchTerm, shareCode }], {
    queryFn: ({ pageParam = 1, queryKey }) =>
      fetchGetOrganizationsSharedProcs({
        queryParams: { page: pageParam, take: PROCS_PER_PAGE, ...queryKey[0] },
      }),
    getNextPageParam: ({ meta }) => (meta.hasNextPage ? meta.page + 1 : false),
    keepPreviousData: true,
  });

  if (!procsData) {
    return null;
  }

  return (
    <PageLayout
      headerContent={
        isAuthenticated && (
          <HeaderWrapper>
            <PageLayoutHeaderGoBackBtn
              path={dashboardGoBackLink}
              label="Go back to dashboard"
            />
          </HeaderWrapper>
        )
      }
      contentStyle={{ padding: 0, paddingTop: 0, paddingBottom: 40 }}
    >
      <PageTitleBanner
        title={organizationData?.name}
        imageUrl={organizationData?.uploadedImageLink}
        bannerColor={organizationData?.theme?.bannerColor}
        isLoading={organizationLoading}
      />
      <DashboardProcsContext.Provider value={{ refetchProcs }}>
        <PageContent>
          <div style={{ padding: '0 25px' }}>
            <Input
              allowClear
              placeholder="Search procs..."
              suffix={<SearchOutlined />}
              onChange={debounce(
                ({ target }) => setSearchTerm(target.value),
                500,
              )}
              style={{ marginBottom: 25 }}
            />
            {!procsData.pages[0]?.data?.length && (
              <Empty
                image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                styles={{
                  image: { height: '100%', maxHeight: 180, marginInline: 0 },
                }}
                description="There's no procs to show"
                style={{
                  width: '100%',
                  height: '60vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              />
            )}
            <Grid>
              {procsData?.pages.map((page, pageIndex) => (
                <Fragment key={pageIndex}>
                  {page.data.map((item) => (
                    <ProcCard key={item.id} proc={item} type="discover" />
                  ))}
                </Fragment>
              ))}
            </Grid>
            {hasNextPage && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '50px 0 20px 0',
                }}
              >
                <Button type="primary" onClick={() => fetchNextPage()}>
                  Load more
                </Button>
              </div>
            )}
          </div>

          <ChatButton
            type="primary"
            shape="circle"
            icon={<MessageOutlined />}
            onClick={() => setChatVisible(!chatVisible)}
          />

          {chatVisible && (
            <ChatWrapper>
              <OrganizationChat
                orgId={organizationData?.id}
                chatMode={ChatMode.User}
                isOpenByDefault={true}
                isCollapsible={false}
              />
            </ChatWrapper>
          )}
        </PageContent>
      </DashboardProcsContext.Provider>
    </PageLayout>
  );
};

const ChatButton = styled(Button)`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 56px !important;
  height: 56px;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);

  svg {
    font-size: 24px;
  }
`;

const ChatWrapper = styled.div`
  position: fixed;
  right: 24px;
  bottom: 96px;
  width: 450px;
  max-width: 600px;
  z-index: 998;
  background: white;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100% - 48px);
    max-width: 600px;
  }
`;

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  grid-gap: 20px;
  align-items: stretch;
`;

const Empty = styled(AntdEmpty)`
  margin-inline: 0;
`;

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageContent = styled.div`
  position: relative;
`;
